import {
  RouteLocationNormalized,
  RouteLocationRaw,
} from 'vue-router';

import { useLogout } from '@composables/use-session-actions';
import { resetTenantSpecificStores } from '@/stores';
import { useTenantAppStore } from '@stores/tenant-app-store';
import { useUserAppStore } from '@stores/user-app-store';
import { useAuthStore } from '@stores/auth-store';
import { useTenantStore } from '@stores/tenant-store';
import { useTenantUserStore } from '@stores/tenant-user-store';
import { useToastStore } from '@stores/toast-store';
import { ENTRY_POINTS } from '@constants/entry-points';

type GuardFunctionType = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
) => void;

export class GuardError extends Error {
  callback: () => void;
  redirectTo: RouteLocationRaw;

  constructor({ message, redirectTo, callback = () => {} }) {
    super(message);
    this.redirectTo = redirectTo;
    this.callback = callback;
    this.name = this.constructor.name;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

const authGuard: GuardFunctionType = async (to) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    const userAppStore = useUserAppStore();
    if (!userAppStore.isInitialized) {
      await userAppStore.initialize();
    }

    const authStore = useAuthStore();
    if (!authStore.authorized) {
      throw new GuardError({
        message: 'Auth guard error',
        redirectTo: ENTRY_POINTS.PATH.LOGIN_TOP(),
        callback: async () => {
          const { softLogout } = await useLogout();
          await softLogout();
          const toastStore = await useToastStore();
          await toastStore.warn('Access Denied: Unauthorized.');
        },
      });
    }
  }
};

const tenantGuard: GuardFunctionType = async (to) => {
  if (to.matched.some((record) => record.meta.requiresTenant)) {
    const tenantAppStore = useTenantAppStore();
    if (!tenantAppStore.isInitialized) {
      await tenantAppStore.initialize();
    }

    const tenantStore = useTenantStore();
    if (!tenantStore.isSelected) {
      throw new GuardError({
        message: 'Tenant guard error.',
        redirectTo: ENTRY_POINTS.PATH.USER_HOME(),
        callback: () => {
          resetTenantSpecificStores();
          const toastStore = useToastStore();
          toastStore.warn('Access Denied: Select workspace.');
        },
      });
    }
  }
};

const tenantAdminGuard: GuardFunctionType = (to) => {
  if (to.matched.some((record) => record.meta.requiresTenantAdmin)) {
    const tenantUserStore = useTenantUserStore();
    if (!tenantUserStore.isAdmin) {
      throw new GuardError({
        message: 'Tenant admin guard error.',
        redirectTo: ENTRY_POINTS.PATH.TENANT_HOME(),
        callback: () => {
          const toastStore = useToastStore();
          toastStore.warn('Access Denied: Admins only.');
        },
      });
    }
  }
};

const enterpriseLicenseGuard: GuardFunctionType = (to) => {
  if (to.matched.some((record) => record.meta.requiresEnterpriseLicense)) {
    const tenantStore = useTenantStore();
    if (!tenantStore.isEnterprise) {
      throw new GuardError({
        message: 'Enterprise license guard error.',
        redirectTo: ENTRY_POINTS.PATH.TENANT_HOME(),
        callback: () => {
          const toastStore = useToastStore();
          toastStore.warn('Access Denied: Requires enterprise license.');
        },
      });
    }
  }
};

const guards: ReadonlyArray<GuardFunctionType> = (
  Object.freeze([
    authGuard,
    tenantGuard,
    tenantAdminGuard,
    enterpriseLicenseGuard,
  ])
);

export default guards;
